import React, { useState, useEffect, useRef, useCallback } from 'react';
import './Module.css';
import SummaryModal from './SummaryModal';
import { logTrendInteraction, logTrendImpression } from '../utils/tracking';
import { buildApiUrl } from '../utils/api';
import { fetchJsonWithRetry } from '../utils/apiWithRetry';

function Module({ module }) {
  const [themes, setThemes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTheme, setSelectedTheme] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [hoveredCard, setHoveredCard] = useState(null);
  const [hoverTimer, setHoverTimer] = useState(null);
  const scrollContainerRef = useRef(null);
  const carouselContainerRef = useRef(null);
  const [cardsPerPage, setCardsPerPage] = useState(4);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const [rightButtonPosition, setRightButtonPosition] = useState('8px');
  // Track which trend IDs have already been logged to prevent duplicates
  const loggedImpressionsRef = useRef(new Set());

  useEffect(() => {
    // Fetch themes for this module from Django API with retry logic
    fetchJsonWithRetry(buildApiUrl(`/api/modules/${module.id}/themes/`), {}, 3)
      .then(data => {
        // Debug logging (development only)
        if (process.env.NODE_ENV === 'development') {
          console.log(`Module ${module.id} - API response:`, {
            themesCount: data.themes?.length || 0,
            sampleTheme: data.themes?.[0] ? {
              title: data.themes[0].title?.substring(0, 50),
              hasProductTable: !!data.themes[0].product_table,
              productTableLength: data.themes[0].product_table?.length || 0
            } : null
          });
        }
        
        setThemes(data.themes || []);
        setLoading(false);
      })
      .catch(error => {
        console.error(`Error fetching themes for ${module.id}:`, error);
        setLoading(false);
      });
    
    // Refresh themes every 30 seconds to check for new topics
    const interval = setInterval(() => {
      fetchJsonWithRetry(buildApiUrl(`/api/modules/${module.id}/themes/`), {}, 2) // 2 retries for background refresh
        .then(data => {
          setThemes(data.themes || []);
        })
        .catch(error => {
          console.error(`Error refreshing themes for ${module.id}:`, error);
        });
    }, 30000); // 30 seconds
    
    return () => clearInterval(interval);
  }, [module.id]);

  // Track trend impressions when cards become visible
  // Use a ref to track if observer is already set up to prevent re-setup on theme refreshes
  const observerSetupRef = useRef(false);
  
  useEffect(() => {
    if (themes.length === 0 || loading || !module.vertical_id) {
      return;
    }
    
    // Don't set up observer if page is not visible
    if (document.hidden || document.visibilityState !== 'visible') {
      return;
    }
    
    // Prevent re-setup if observer is already active
    if (observerSetupRef.current) {
      return;
    }
    
    let impressionObserver = null;
    let isPageVisible = true;
    
    // Listen for visibility changes and disable observer when page becomes hidden
    const handleVisibilityChange = () => {
      const wasVisible = isPageVisible;
      isPageVisible = !document.hidden && document.visibilityState === 'visible';
      
      if (!isPageVisible && wasVisible && impressionObserver) {
        // Disconnect observer when page becomes hidden
        impressionObserver.disconnect();
        observerSetupRef.current = false;
      } else if (isPageVisible && !wasVisible) {
        observerSetupRef.current = false;
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    // Mark as set up before timeout to prevent race conditions
    observerSetupRef.current = true;
    
    // Wait a bit for DOM to be ready
    const timeoutId = setTimeout(() => {
      // Double-check visibility before setting up observer
      if (document.hidden || document.visibilityState !== 'visible') {
        observerSetupRef.current = false;
        return;
      }
      
      const cardElements = document.querySelectorAll(`#module-${module.id} .theme-card`);
      if (cardElements.length === 0) {
        observerSetupRef.current = false;
        return;
      }

      // Best practice: Use 0.5 threshold (50% visible) - IAB viewability standard
      // This ensures the card is meaningfully visible before logging impression
      impressionObserver = new IntersectionObserver(
        (entries) => {
          // Check if page is visible - if not, ignore all entries
          if (document.hidden || document.visibilityState !== 'visible') {
            return;
          }
          
          entries.forEach((entry) => {
            // Only log when element is intersecting AND meets visibility threshold
            if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
              // Check page visibility one more time before logging
              if (document.hidden || document.visibilityState !== 'visible') {
                return;
              }
              
              const cardElement = entry.target;
              const cardIndex = Array.from(cardElements).indexOf(cardElement);
              const theme = themes[cardIndex];
              
              // Create unique key for this impression (trend_id + card_position)
              const impressionKey = theme?.id ? `${theme.id}-${cardIndex}` : null;
              
              // Check if already logged (client-side deduplication)
              if (impressionKey && loggedImpressionsRef.current.has(impressionKey)) {
                console.log(`[Trend Impressions] Skipping duplicate: ${impressionKey}`);
                impressionObserver.unobserve(cardElement);
                return;
              }
              
              console.log(`[Trend Impressions] Card ${cardIndex} intersecting:`, {
                hasTheme: !!theme,
                themeId: theme?.id,
                hasVerticalId: !!module.vertical_id,
                verticalId: module.vertical_id,
                visibilityRatio: entry.intersectionRatio,
                impressionKey,
                pageVisible: !document.hidden
              });
              
              if (theme && typeof theme === 'object' && theme.id && module.vertical_id) {
                // Mark as logged immediately to prevent duplicates
                loggedImpressionsRef.current.add(impressionKey);
                
                // Calculate scroll set (which page of cards)
                const scrollSet = Math.floor(cardIndex / cardsPerPage);
                
                // Determine viewport visibility level based on intersection ratio
                // Since we only log when >= 0.5, we can categorize the visibility
                const visibilityRatio = entry.intersectionRatio;
                const viewportPosition = visibilityRatio >= 0.9 ? 'fully_visible' : 
                                        visibilityRatio >= 0.75 ? 'mostly_visible' : 
                                        'partially_visible'; // 0.5 - 0.74
                
                // Log impression
                logTrendImpression(
                  theme.id,
                  module.vertical_id,
                  cardIndex, // Absolute position in carousel
                  scrollSet,
                  viewportPosition
                );
                
                // Unobserve after logging to avoid duplicate logs
                impressionObserver.unobserve(cardElement);
              }
            }
          });
        },
        {
          threshold: 0.5, // 50% visible - IAB viewability standard for meaningful impressions
          rootMargin: '0px' // No margin - use actual viewport
        }
      );

      // Only observe cards that haven't been logged yet
      cardElements.forEach((card, index) => {
        const theme = themes[index];
        const impressionKey = theme?.id ? `${theme.id}-${index}` : null;
        
        // Skip if already logged
        if (impressionKey && loggedImpressionsRef.current.has(impressionKey)) {
          return;
        }
        
        impressionObserver.observe(card);
      });

      // Store observer for cleanup
      return () => {
        cardElements.forEach((card) => {
          impressionObserver.unobserve(card);
        });
      };
    }, 500); // Small delay to ensure DOM is ready
    
    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (impressionObserver) {
        impressionObserver.disconnect();
      }
      observerSetupRef.current = false; // Reset on cleanup
    };
  }, [themes.length, module.id, module.vertical_id, cardsPerPage, loading]); // Only depend on themes.length, not themes array itself

  // Calculate cards per page based on screen size and button position
  useEffect(() => {
    const calculateCardsPerPage = () => {
      if (!carouselContainerRef.current) return;
      
      const containerWidth = carouselContainerRef.current.offsetWidth;
      let cards = 4; // Default for 14" screens
      
      if (containerWidth >= 1800) {
        cards = 5; // Extra large screens
      } else if (containerWidth >= 1400) {
        cards = 4; // Large screens (16" MacBook, desktop)
      } else if (containerWidth >= 1024) {
        cards = 4; // 14" MacBook Pro (target)
      } else if (containerWidth >= 768) {
        cards = 3; // Tablets
      } else if (containerWidth >= 480) {
        cards = 2; // Mobile landscape
      } else {
        cards = 1; // Mobile portrait
      }
      
      setCardsPerPage(cards);
      
      // Calculate right button position at edge of last visible card
      const gap = 24; // Match CSS gap
      const cardWidth = (containerWidth - (gap * (cards - 1))) / cards;
      const buttonWidth = 48; // Button width
      const buttonPosition = (cardWidth * cards) + (gap * (cards - 1)) - buttonWidth / 2;
      setRightButtonPosition(`${buttonPosition}px`);
    };

    calculateCardsPerPage();
    window.addEventListener('resize', calculateCardsPerPage);
    return () => window.removeEventListener('resize', calculateCardsPerPage);
  }, [themes]);

  // Check scroll position to show/hide buttons
  useEffect(() => {
    // Copy ref to variable to avoid stale closure in cleanup
    const scrollContainer = scrollContainerRef.current;
    
    const checkScrollPosition = () => {
      if (!scrollContainer) return;
      
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainer;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10); // 10px threshold
    };

    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', checkScrollPosition);
      checkScrollPosition(); // Initial check
    }

    return () => {
      if (scrollContainer) {
        scrollContainer.removeEventListener('scroll', checkScrollPosition);
      }
    };
  }, [themes, cardsPerPage]);

  // Handler to close modal with animation
  const handleModalClose = useCallback(() => {
    setIsModalOpen(false);
    setHoveredCard(null);
    if (hoverTimer) {
      clearTimeout(hoverTimer);
      setHoverTimer(null);
    }
  }, [hoverTimer]);

  // Add ESC key listener to close modal
  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === 'Escape' && isModalOpen) {
        // Trigger the smooth close animation by calling the modal's close handler
        handleModalClose();
      }
    };

    // Add event listener when modal is open
    if (isModalOpen) {
      document.addEventListener('keydown', handleEscKey);
    }

    // Cleanup
    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [isModalOpen, handleModalClose]);

  return (
    <section className={`module module-theme-${module.id}`} id={`module-${module.id}`} data-module-theme={module.id}>
      <div className="module-header">
        <h2>{module.title}</h2>
        <span className={`module-badge ${module.status}`}>
          {module.status}
        </span>
      </div>
      <div className="module-content">
        <div className="themes-container">
          {loading ? (
            <p className="placeholder-text">Loading themes...</p>
          ) : themes.length === 0 ? (
            <p className="placeholder-text">Major themes and topics will appear here...</p>
          ) : (
            <>
              {/* Horizontal scroll carousel - Netflix/Amazon style */}
              <div className="themes-carousel-container" ref={carouselContainerRef}>
                <div className="themes-carousel-wrapper">
                  <div 
                    className="themes-carousel" 
                    ref={scrollContainerRef}
                    style={{
                      '--cards-per-page': cardsPerPage
                    }}
                  >
                    {themes.map((theme, index) => {
                      const themeTitle = typeof theme === 'string' ? theme : theme.title;
                      const themeSummary = typeof theme === 'object' ? theme.summary : null;
                      const themeImageUrl = typeof theme === 'object' ? theme.image_url : null;
                      const themeProductTable = typeof theme === 'object' ? theme.product_table : null;
                      const hasSummary = themeSummary && themeSummary.trim().length > 0;
                      const hasImage = themeImageUrl && themeImageUrl.trim().length > 0;
                      const cardId = `card-${index}`;
                      const isHovered = hoveredCard === cardId;
                      
                      const handleMouseEnter = () => {
                        // Don't trigger if modal is already open
                        if (hasSummary && !isModalOpen) {
                          setHoveredCard(cardId);
                          // Delay modal opening to allow image to load first
                          // If image exists, wait longer (2s) for image load, otherwise 750ms
                          const delay = hasImage ? 2000 : 750;
                          const timer = setTimeout(() => {
                            // Include ALL theme properties, especially product_table
                            const themeData = {
                              title: themeTitle, 
                              summary: themeSummary,
                              trend_section: typeof theme === 'object' ? theme.trend_section : null,
                              product_table: themeProductTable,
                              summary_sections: typeof theme === 'object' ? theme.summary_sections : null,
                              metadata: typeof theme === 'object' ? theme.metadata : null,
                              image_url: themeImageUrl,
                              video_url: typeof theme === 'object' ? theme.video_url : null,
                              id: typeof theme === 'object' ? theme.id : null
                            };
                            setSelectedTheme(themeData);
                            setIsModalOpen(true);
                            setHoveredCard(null); // Reset hover state
                            
                            // Log trend interaction
                            if (themeData.id) {
                              logTrendInteraction(themeData.id, 'expand', {
                                module_id: module.id,
                                title: themeData.title
                              });
                            }
                          }, delay);
                          setHoverTimer(timer);
                        }
                      };
                      
                      const handleMouseLeave = () => {
                        // Don't clear hover state if modal is open
                        if (!isModalOpen) {
                          setHoveredCard(null);
                          // Clear timer if user leaves before modal opens
                          if (hoverTimer) {
                            clearTimeout(hoverTimer);
                            setHoverTimer(null);
                          }
                        }
                      };
                      
                      return (
                        <div 
                          key={index} 
                          className={`theme-card ${isHovered && !isModalOpen ? 'flipped' : ''}`}
                          onMouseEnter={handleMouseEnter}
                          onMouseLeave={handleMouseLeave}
                        >
                          <div className="theme-card-inner">
                            <div className="theme-card-front">
                              <div className="theme-card-content">
                                <h3 className="theme-card-title">{themeTitle}</h3>
                                {hasSummary && (
                                  <div className="theme-card-hint">
                                    <span>Hover to read more</span>
                                  </div>
                                )}
                              </div>
                            </div>
                            <div className="theme-card-back">
                              <div className="theme-card-content">
                                {hasImage ? (
                                  <div className="theme-card-image-container">
                                    <img 
                                      src={themeImageUrl} 
                                      alt={themeTitle}
                                      className="theme-card-image"
                                      onLoad={() => {
                                        if (process.env.NODE_ENV === 'development') {
                                          console.log(`Image loaded: ${themeImageUrl}`);
                                        }
                                      }}
                                      onError={(e) => {
                                        console.error(`Image failed to load: ${themeImageUrl}`);
                                        e.target.style.display = 'none';
                                      }}
                                    />
                                  </div>
                                ) : (
                                  <p className="theme-card-loading">Loading summary...</p>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
                
                {/* Navigation buttons - positioned at edge of visible cards */}
                {canScrollLeft && (
                  <button 
                    className="carousel-button carousel-button-left" 
                    aria-label="Previous cards"
                    onClick={() => {
                      if (scrollContainerRef.current && carouselContainerRef.current) {
                        // Calculate exact scroll amount: width of one page of cards
                        const containerWidth = carouselContainerRef.current.offsetWidth;
                        const gap = 24; // Match CSS gap
                        const cardWidth = (containerWidth - (gap * (cardsPerPage - 1))) / cardsPerPage;
                        const scrollAmount = cardWidth * cardsPerPage + gap * (cardsPerPage - 1);
                        
                        // Add smooth scroll with easing
                        scrollContainerRef.current.scrollTo({ 
                          left: scrollContainerRef.current.scrollLeft - scrollAmount, 
                          behavior: 'smooth' 
                        });
                      }
                    }}
                  >
                    ←
                  </button>
                )}
                {canScrollRight && (
                  <button 
                    className="carousel-button carousel-button-right" 
                    aria-label="Next cards"
                    style={{
                      left: rightButtonPosition,
                      right: 'auto'
                    }}
                    onClick={() => {
                      if (scrollContainerRef.current && carouselContainerRef.current) {
                        // Calculate exact scroll amount: width of one page of cards
                        const containerWidth = carouselContainerRef.current.offsetWidth;
                        const gap = 24; // Match CSS gap
                        const cardWidth = (containerWidth - (gap * (cardsPerPage - 1))) / cardsPerPage;
                        const scrollAmount = cardWidth * cardsPerPage + gap * (cardsPerPage - 1);
                        
                        // Add smooth scroll with easing
                        scrollContainerRef.current.scrollTo({ 
                          left: scrollContainerRef.current.scrollLeft + scrollAmount, 
                          behavior: 'smooth' 
                        });
                      }
                    }}
                  >
                    →
                  </button>
                )}
              </div>
              <SummaryModal
                isOpen={isModalOpen}
                onClose={handleModalClose}
                title={selectedTheme?.title || ''}
                summary={selectedTheme?.summary || ''}
                trendSection={selectedTheme?.trend_section || null}
                productTable={selectedTheme?.product_table || null}
                moduleId={module.id}
                videoUrl={selectedTheme?.video_url || null}
                trendId={selectedTheme?.id || null}
                summarySections={selectedTheme?.summary_sections || []}
              />
              {/* Debug: Log selected theme data (development only) */}
              {isModalOpen && process.env.NODE_ENV === 'development' && console.log('Module - selectedTheme:', {
                title: selectedTheme?.title,
                hasSummary: !!selectedTheme?.summary,
                hasProductTable: !!selectedTheme?.product_table,
                productTableLength: selectedTheme?.product_table?.length || 0,
                productTablePreview: selectedTheme?.product_table?.substring(0, 100) || 'null'
              })}
            </>
          )}
        </div>
      </div>
    </section>
  );
}

export default Module;

